import { useCallback, useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import { buildFullLink, getPath } from "common/routing";
import {
  arrayHasAtLeastTwoItems,
  arrayHasExactlyOneItem,
} from "common/utils/arrays";
import { getClientAppPrefix } from "common/utils/url";
import { UserAuthMethod } from "common-client/generated/graphql";
import {
  useGetUserAccountsWithAuthInfoLazyQuery,
  UserWithAuthInfoFragment,
} from "../../../generated/graphql";
import {
  IDENTIFY_USER_SCREEN_DISPLAY,
  IdentifyUserReducer,
  useIdentifyUserReducer,
} from "../../../hooks/useIdentifyUserFlow";
import {
  EmailParamInput,
  PrevLocationParamInput,
  useEmailParamParser,
  useLogoutReasonParamParser,
  usePrevLocationParamParser,
  useSsoErrorParamParser,
} from "../../../utils/params";
import FullPageBackgroundWithLogo from "../../Common/FullPageBackgroundWithLogo";
import { Container } from "../CommonLoginComponents/__styles__/FormWithFullPageBackground";
import { AccountPicker } from "./AccountPicker";
import { ProvideEmail } from "./ProvideEmail";

export type LoginFormStructure = {
  email?: Maybe<string>;
};

export const IdentifyUser = () => {
  const history = useHistory();
  const logoutReasonError = useLogoutReasonParamParser();
  const ssoError = useSsoErrorParamParser();
  const startingEmail = useEmailParamParser("login");

  const startingError = ssoError ?? logoutReasonError;

  const [state, dispatch] = useIdentifyUserReducer({
    defaultValue:
      startingEmail ?
        {
          currentScreen: IDENTIFY_USER_SCREEN_DISPLAY.ACCOUNT_PICKER,
          email: startingEmail,
          accounts: [],
          error: startingError ?? null,
        }
      : {
          currentScreen: IDENTIFY_USER_SCREEN_DISPLAY.PROVIDE_EMAIL,
          email: null,
          accounts: [],
          error: startingError ?? null,
        },
    useReducer: useReducer as IdentifyUserReducer,
  });

  const prev = usePrevLocationParamParser("login");

  const redirectToPasswordLogin = useCallback(
    (email: string, subdomain: string) => {
      const prefix = getClientAppPrefix(subdomain);
      const loginUrl = buildFullLink("provideCredentials", {
        params: {},
        queryParams: { prev, email } satisfies PrevLocationParamInput &
          EmailParamInput,
        prefix,
      });
      window.location.href = loginUrl;
    },
    [prev],
  );

  const redirectToSSOProvider = useCallback(
    (accountId: string, providerId: string) => {
      window.location.href = `/auth/sso/initiate?accountId=${accountId}&providerId=${providerId}`;
    },
    [],
  );

  const handleSSOLogin = useCallback(
    (userAccount: UserWithAuthInfoFragment) => {
      const { account, ssoProviderId } = userAccount;

      if (!ssoProviderId) {
        dispatch({
          type: "setError",
          data: {
            error: new Error(
              "Please accept your invitation to access this account.",
            ),
          },
        });
        return;
      }

      redirectToSSOProvider(account.id, ssoProviderId);
    },
    [dispatch, redirectToSSOProvider],
  );

  const onSelectAccount = useCallback(
    ({
      email,
      userAccount,
    }: {
      email: string;
      userAccount: UserWithAuthInfoFragment;
    }) => {
      const { account, authMethod } = userAccount;

      if (authMethod === UserAuthMethod.PASSWORD) {
        redirectToPasswordLogin(email, account.subdomain);
        return;
      }

      handleSSOLogin(userAccount);
    },
    [redirectToPasswordLogin, handleSSOLogin],
  );

  const [getUserAccounts, { loading, variables }] =
    useGetUserAccountsWithAuthInfoLazyQuery({
      onError: () => {
        dispatch({
          type: "setError",
          data: {
            error: new Error(
              "An error occurred. Please try again. If the problem persists, please email us at support@withforerunner.com.",
            ),
          },
        });
      },
      onCompleted: data => {
        const email = variables!.email;

        const accounts = data.getUserAccountsWithAuthInfo;

        if (arrayHasAtLeastTwoItems(accounts)) {
          dispatch({
            type: "displayAccountPicker",
            data: {
              accounts,
              email,
            },
          });
        } else if (arrayHasExactlyOneItem(accounts)) {
          onSelectAccount({
            email,
            userAccount: accounts[0],
          });
        } else {
          dispatch({
            type: "setError",
            data: {
              error: new Error(
                "An error occurred while processing your request. Please try again or contact support@withforerunner.com for help.",
              ),
            },
          });
        }
      },
    });

  useEffect(() => {
    if (startingEmail) {
      void getUserAccounts({
        variables: { email: startingEmail },
      });
    }
  }, [startingEmail, getUserAccounts]);

  useEffect(() => {
    if (ssoError) {
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("message");
      history.replace({
        pathname: getPath("login"),
        search: url.searchParams.toString(),
      });
    }
  }, [ssoError, history]);

  return (
    <FullPageBackgroundWithLogo>
      <Container>
        {state.currentScreen === IDENTIFY_USER_SCREEN_DISPLAY.PROVIDE_EMAIL && (
          <ProvideEmail
            state={state}
            onSubmit={async email => {
              await getUserAccounts({ variables: { email } });
            }}
            loading={loading}
          />
        )}
        {state.currentScreen ===
          IDENTIFY_USER_SCREEN_DISPLAY.ACCOUNT_PICKER && (
          <AccountPicker
            state={state}
            dispatch={dispatch}
            onSubmit={onSelectAccount}
          />
        )}
      </Container>
    </FullPageBackgroundWithLogo>
  );
};

export default IdentifyUser;
